import i18n from "i18next";
import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { useLoyaltyAllowedMatrixDeleteMutation } from "src/app/features/LoyaltyAllowedMatrix/services/server/LoyaltyAllowedMatrixApi";
import type { LoyaltyAllowedMatrixEntity } from "src/app/features/LoyaltyAllowedMatrix/types/LoyaltyAllowedMatrixEntity";
import type { ExtendedColumnsType } from "../../../../../components";
import { Checkbox, TextInput } from "../../../../../components/Form/formComponents";
import { type AntCustomFormField, type CommonFieldAttributes } from "../../../../../components/Form/types/types";
import { PropertyTable } from "../../../../../components/Table/components/PropertyTable";
import { TableAddNewLineButton } from "../../../../../components/Table/components/PropertyTable/TableAddNewLineButton";
import { Actions } from "../../../../../components/Table/types/types";
import { showSuccessNotification, showWarningNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";

export interface CardCombinationsTableProps extends AntCustomFormField<LoyaltyAllowedMatrixEntity[]>, CommonFieldAttributes {}

export const CardCombinationsTable: FC<CardCombinationsTableProps> = ({ value = [], onChange, readOnly }) => {
    const dispatch = useDispatch();
    // Assign a unique rowId to each row (persisted or new)
    const assignRowIds = (array: LoyaltyAllowedMatrixEntity[]) =>
        array.map((item) => ({
            ...item,
            rowId: item.rowId ?? item.id ?? uuidv4(),
        }));

    const appendRowId = (array: LoyaltyAllowedMatrixEntity[]) =>
        assignRowIds(
            array.slice().sort((a, b) => {
                if (a.id === undefined && b.id !== undefined) return 1;
                if (a.id !== undefined && b.id === undefined) return -1;
                return Number(a.id ?? 0) - Number(b.id ?? 0);
            }),
        );

    // Use rowId for all row operations
    const onInput = (rowId: string | number, key: keyof LoyaltyAllowedMatrixEntity, newValue: string | boolean) => {
        const newData = value.map((row) => {
            if (row.rowId !== rowId) return row;
            return { ...row, [key]: newValue };
        });
        onDataChanged(newData);
    };

    const getColumnsConfig = (t: Function): ExtendedColumnsType<LoyaltyAllowedMatrixEntity>[] => {
        return [
            {
                title: t("loyaltyMatrix.paymentCardCircuit"),
                key: "paymentCardCircuit",
                dataIndex: "paymentCardCircuit",
                render: (_, record) => (
                    <TextInput
                        name={`paymentCardCircuit-${record.rowId}`}
                        label=""
                        value={record.paymentCardCircuit}
                        readOnly={false}
                        onChange={(newValue) => onInput(record.rowId!, "paymentCardCircuit", newValue)}
                    />
                ),
            },
            {
                title: t("loyaltyMatrix.loyaltyCardCircuit"),
                key: "loyaltyCardCircuit",
                dataIndex: "loyaltyCardCircuit",
                render: (_, record) => (
                    <TextInput
                        name={`loyaltyCardCircuit-${record.rowId}`}
                        label=""
                        value={record.loyaltyCardCircuit}
                        readOnly={false}
                        onChange={(newValue) => onInput(record.rowId!, "loyaltyCardCircuit", newValue)}
                    />
                ),
            },
            {
                title: t("loyaltyMatrix.allowed"),
                key: "allowed",
                dataIndex: "allowed",
                render: (_, record) => (
                    <Checkbox
                        name={`allowed-${record.rowId}`}
                        label=""
                        value={record.allowed ?? false}
                        readOnly={false}
                        onChange={(checked) => onInput(record.rowId!, "allowed", checked)}
                    />
                ),
            },
            {
                title: t("loyaltyMatrix.virtualLoyaltyAllowed"),
                key: "virtualLoyaltyAllowed",
                dataIndex: "virtualLoyaltyAllowed",
                render: (_, record) => (
                    <Checkbox
                        name={`virtualLoyaltyAllowed-${record.rowId}`}
                        label=""
                        value={record.virtualLoyaltyAllowed ?? false}
                        readOnly={false}
                        onChange={(checked) => onInput(record.rowId!, "virtualLoyaltyAllowed", checked)}
                    />
                ),
            },
        ];
    };

    const onDataChanged = useCallback(
        (data: LoyaltyAllowedMatrixEntity[]) => {
            if (onChange) {
                onChange(assignRowIds(data));
            }
        },
        [onChange],
    );

    const addNewLine = () => {
        const emptyTableData = {
            id: undefined,
            paymentCardCircuit: "",
            loyaltyCardCircuit: "",
            allowed: false,
            virtualLoyaltyAllowed: false,
            rowId: uuidv4(),
        } as LoyaltyAllowedMatrixEntity;
        const newTableValue = [...assignRowIds(value), emptyTableData];
        onDataChanged(newTableValue);
    };

    const [deleteTableItem] = useLoyaltyAllowedMatrixDeleteMutation();

    const deleteCallback = (item: LoyaltyAllowedMatrixEntity) => {
        // if item.id is undefined, it means it is not saved in the DB yet, so we remove it from the table
        if (item.id === undefined) {
            const newTableValue = assignRowIds(value).filter((row) => row.rowId !== item.rowId);
            onDataChanged(newTableValue);
            return;
        }
        deleteTableItem({
            id: item.id,
        })
            .unwrap()
            .then((payload) => performDeleteSuccess(payload))
            .catch(() => {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("common.formDeleteError", {
                            id: item.id,
                        }),
                    }),
                );
            });
        const performDeleteSuccess = (payload: LoyaltyAllowedMatrixEntity) => {
            if (payload) {
                notifyOkDelete(payload);
            }
            onDataChanged(assignRowIds(value.filter((record) => record.id !== item.id)));
        };
    };
    const notifyOkDelete = useCallback(
        (payload: LoyaltyAllowedMatrixEntity) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: "Loyalty Matrix",
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    return (
        <PropertyTable<LoyaltyAllowedMatrixEntity>
            name="cardCombinations_table"
            getColumnsConfig={getColumnsConfig}
            propertyAsRowKey="rowId"
            tableData={appendRowId(value) || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            actions={[Actions.DELETE]}
            actionCallbacks={{ delete: (item?: LoyaltyAllowedMatrixEntity) => item && deleteCallback(item) }}
            customActions={[<TableAddNewLineButton key="cardCombination_add-new-line" callback={addNewLine} />]}
        />
    );
};
